import { MAP_CATEGORY_CLIENT_CODE, MAP_CATEGORY_INDENT_LEVEL } from '@/constants/floor-guide';
import { DUTY_FREE_OTHER_FACILITY_CLIENT_CODES, LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import type { BuildingCategory } from '@/services/dto';
import { useConfigStore } from '@/stores';
import { sortCategories } from '@/utils/floor-guide';
import { findBuildingByMapId } from '@/utils/poi';
import { useCallback, useMemo, useState } from 'react';

/** One map button. Same clientCode (Restroom / Escalator / Elevator, including duty-free) shares every categoryCode. */
export type MergedBuildingCategory = BuildingCategory & {
    categoryCodes: string[];
};

const isMapCategory = (category: BuildingCategory) => {
    const isOtherFacility =
        category?.parentClientCode === MAP_CATEGORY_CLIENT_CODE && category?.indentLevel === MAP_CATEGORY_INDENT_LEVEL;

    const isDutyFreeOtherFacility =
        category?.parentClientCode === LOTTE_CATEGORY_FIELD.DUTY_FREE_OTHER_FACILITY_CODE &&
        DUTY_FREE_OTHER_FACILITY_CLIENT_CODES.has(category?.clientCode);

    return isOtherFacility || isDutyFreeOtherFacility;
};

const mergeCategoriesByClientCode = (categories: BuildingCategory[]): MergedBuildingCategory[] => {
    const groups = new Map<string, MergedBuildingCategory>();

    categories.forEach((category) => {
        if (!isMapCategory(category) || !category.categoryCode) {
            return;
        }

        const groupKey = category.clientCode || category.categoryCode;
        const current = groups.get(groupKey);

        if (!current) {
            groups.set(groupKey, {
                ...category,
                categoryCodes: [category.categoryCode],
            });
            return;
        }

        if (!current.categoryCodes.includes(category.categoryCode)) {
            current.categoryCodes.push(category.categoryCode);
        }
    });

    return Array.from(groups.values());
};

export const getCategoryCodesForButton = (category?: Partial<MergedBuildingCategory> | null): string[] => {
    if (Array.isArray(category?.categoryCodes) && category.categoryCodes.length > 0) {
        return category.categoryCodes;
    }

    return category?.categoryCode ? [category.categoryCode] : [];
};

export const useBuildingCategory = () => {
    const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
    const [selectedCategoryCode, setSelectedCategoryCode] = useState<string | undefined>(undefined);
    const [selectedCategoryCodes, setSelectedCategoryCodes] = useState<string[]>([]);

    // All building categories which has content
    const allBuildingCategories = useMemo(() => {
        if (!buildings) {
            return [];
        }

        const facilityCategories = buildings.flatMap((item) =>
            Array.isArray(item?.filterCategories?.otherFacility) ? item?.filterCategories?.otherFacility : []
        );

        return facilityCategories;
    }, [buildings]);

    const getCategoriesByBuildingId = useCallback(
        (buildingId: string) => {
            if (!buildings) {
                return [];
            }

            const categories = findBuildingByMapId(buildings, buildingId)?.filterCategories?.otherFacility || [];
            return mergeCategoriesByClientCode(categories);
        },
        [buildings]
    );

    // One button per clientCode, carrying every categoryCode that belongs to that button
    const filteredBuildingCategories = useMemo(() => {
        return mergeCategoriesByClientCode(allBuildingCategories);
    }, [allBuildingCategories]);

    const uniqueCategoryByClientCode = filteredBuildingCategories;

    const selectBuildingCategory = useCallback((category?: Partial<MergedBuildingCategory> | null) => {
        const codes = getCategoryCodesForButton(category);
        setSelectedCategoryCode(category?.categoryCode);
        setSelectedCategoryCodes(codes);
    }, []);

    // Find selected category
    const selectedCategory = useMemo(() => {
        if (!selectedCategoryCode) {
            return null;
        }
        return (
            filteredBuildingCategories.find(
                (category) => category.categoryCode === selectedCategoryCode || category.categoryCodes.includes(selectedCategoryCode)
            ) || null
        );
    }, [selectedCategoryCode, filteredBuildingCategories]);

    // Sort building categories
    const sortedBuildingCategories = useMemo(() => {
        return sortCategories(uniqueCategoryByClientCode);
    }, [uniqueCategoryByClientCode]);

    // Group category by client code
    const groupedCategoryByClientCode = useMemo(() => {
        const categoryMap: { [clientCode: string]: string[] } = {};

        filteredBuildingCategories.forEach((category) => {
            if (!category.clientCode) {
                return;
            }
            categoryMap[category.clientCode] = category.categoryCodes;
        });

        return categoryMap;
    }, [filteredBuildingCategories]);

    return {
        uniqueCategoryByClientCode,
        selectedCategoryCode,
        selectedCategoryCodes,
        selectedCategory,
        setSelectedCategoryCode,
        selectBuildingCategory,
        sortedBuildingCategories,
        groupedCategoryByClientCode,
        allBuildingCategories,
        getCategoriesByBuildingId,
    };
};
